"use client"

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react"

type EntityTypeContextType = {
  entityType: string
  setEntityType: (value: string) => void
}

const DEFAULT_ENTITY_TYPE = "WorkOrder"

const EntityTypeContext = createContext<EntityTypeContextType | undefined>(undefined)

export function EntityTypeProvider({ children }: { children: ReactNode }) {
  const [entityType, setEntityTypeState] = useState<string>(DEFAULT_ENTITY_TYPE)

  useEffect(() => {
    if (typeof window === "undefined") return
    const params = new URLSearchParams(window.location.search)
    const fromUrl = params.get("entityType")
    if (fromUrl && fromUrl.trim().length > 0) {
      setEntityTypeState(fromUrl)
    } else {
      // Ensure default is reflected in the URL for bookmarking/sharing
      params.set("entityType", DEFAULT_ENTITY_TYPE)
      const newUrl = `${window.location.pathname}?${params.toString()}`
      window.history.replaceState({}, "", newUrl)
    }
  }, [])

  useEffect(() => {
    const onPopState = () => {
      if (typeof window === "undefined") return
      const params = new URLSearchParams(window.location.search)
      const fromUrl = params.get("entityType")
      if (fromUrl && fromUrl !== entityType) {
        setEntityTypeState(fromUrl)
      }
    }
    window.addEventListener("popstate", onPopState)
    return () => window.removeEventListener("popstate", onPopState)
  }, [entityType])

  const setEntityType = (value: string) => {
    setEntityTypeState(value)
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search)
      params.set("entityType", value)
      const newUrl = `${window.location.pathname}?${params.toString()}`
      window.history.replaceState({}, "", newUrl)
    }
  }

  const contextValue = useMemo(() => ({ entityType, setEntityType }), [entityType])

  return (
    <EntityTypeContext.Provider value={contextValue}>{children}</EntityTypeContext.Provider>
  )
}

export function useEntityType() {
  const ctx = useContext(EntityTypeContext)
  if (!ctx) throw new Error("useEntityType must be used within an EntityTypeProvider")
  return ctx
}


